import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Outfit } from '../../types';
import { useThemeStore } from '../../stores/themeStore';
import { GRADIENT_ACCENTS, DISPLAY_FONT } from '../../theme';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface HeroCarouselProps {
  outfits: Outfit[];
  autoAdvanceMs?: number;
  tagline?: string;
}

const DEFAULT_TAGLINE = "What's your fit today?";
const AUTO_ADVANCE_MS = 5000;

const HeroCarousel: React.FC<HeroCarouselProps> = ({
  outfits,
  autoAdvanceMs = AUTO_ADVANCE_MS,
  tagline = DEFAULT_TAGLINE,
}) => {
  const navigate = useNavigate();
  const palette = useThemeStore((s) => s.palette);
  const gradient = GRADIENT_ACCENTS[palette].primary;
  const prefersReducedMotion = useReducedMotion();

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const count = outfits.length;
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    if (count <= 1 || isPaused) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % count);
    }, autoAdvanceMs);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [count, isPaused, autoAdvanceMs]);

  const goTo = useCallback((i: number) => setActiveIndex(i), []);
  const goNext = useCallback(() => setActiveIndex((p) => (p + 1) % count), [count]);
  const goPrev = useCallback(() => setActiveIndex((p) => (p - 1 + count) % count), [count]);
  const handleClick = useCallback(() => {
    if (count > 0) navigate('/outfit/' + outfits[activeIndex].id);
  }, [navigate, outfits, activeIndex, count]);

  if (count === 0) {
    return (
      <Box sx={{
        width: '100%', height: { xs: 200, md: 300 }, borderRadius: 3,
        overflow: 'hidden', mb: 4, background: gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography variant="h3" sx={{
          fontFamily: DISPLAY_FONT, fontWeight: 700, color: '#fff',
          textAlign: 'center', px: 3, textShadow: '0 2px 12px rgba(0,0,0,0.3)',
        }}>
          {tagline}
        </Typography>
      </Box>
    );
  }

  const current = outfits[activeIndex];
  const rating = current.ratings?.averageRating?.toFixed(1) || '—';
  const totalRatings = current.ratings?.totalRatings || 0;

  return (
    <Box
      onMouseEnter={() => !isTouchDevice && setIsPaused(true)}
      onMouseLeave={() => !isTouchDevice && setIsPaused(false)}
      sx={{
        position: 'relative', width: '100%', borderRadius: 3,
        overflow: 'hidden', mb: 4, cursor: 'pointer',
        display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        height: { xs: 'auto', md: 380 },
        background: gradient,
      }}
      onClick={handleClick}
    >
      {/* Left: outfit image — contained so full outfit is visible */}
      <Box sx={{
        width: { xs: '100%', md: '45%' }, height: { xs: 320, md: '100%' },
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.3)', flexShrink: 0, overflow: 'hidden',
      }}>
        <Box
          component="img"
          src={current.imageUrl}
          alt={current.title || 'Trending outfit'}
          sx={{
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            transition: prefersReducedMotion ? 'none' : 'opacity 300ms ease',
          }}
        />
      </Box>

      {/* Right: info panel */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', p: { xs: 2.5, md: 4 }, position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 100%)',
          zIndex: 0,
        },
        '& > *': { position: 'relative', zIndex: 1 },
      }}>
        <Typography variant="h4" sx={{
          fontFamily: DISPLAY_FONT, fontWeight: 700, color: '#fff',
          mb: 1.5, textShadow: '0 2px 12px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)',
          fontSize: { xs: '1.4rem', md: '2rem' },
        }}>
          {tagline}
        </Typography>

        <Typography variant="h6" sx={{
          color: '#fff', fontWeight: 600, mb: 1,
          textShadow: '0 1px 6px rgba(0,0,0,0.5)',
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {current.title || current.description || 'Untitled Outfit'}
        </Typography>

        {current.aiAnalysis?.description && (
          <Typography variant="body2" sx={{
            color: 'rgba(255,255,255,0.9)', mb: 1.5,
            textShadow: '0 1px 4px rgba(0,0,0,0.4)',
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {current.aiAnalysis.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <Chip label={current.eventType || 'Casual'} size="small" sx={{
            backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600,
          }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
            {'⭐'} {rating} · {totalRatings} ratings
          </Typography>
        </Box>

        {/* Dot indicators */}
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          {outfits.map((_, i) => (
            <Box
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              role="button"
              aria-label={'Go to slide ' + (i + 1)}
              sx={{
                width: i === activeIndex ? 24 : 8, height: 8, borderRadius: 4,
                backgroundColor: i === activeIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                transition: prefersReducedMotion ? 'none' : 'width 200ms ease, background-color 200ms ease',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Navigation arrows */}
      {count > 1 && (
        <>
          <IconButton
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous slide"
            sx={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              zIndex: 3, color: '#fff', backgroundColor: 'rgba(0,0,0,0.3)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next slide"
            sx={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              zIndex: 3, color: '#fff', backgroundColor: 'rgba(0,0,0,0.3)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default HeroCarousel;
